import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import { colors } from "app/popleads/components/ui-kit/colors";
import SelectableTaskCard from "app/popleads/modules/extension/components/SelectableTaskCard";
import DateHeader from "app/popleads/components/ui-kit/DateHeader";
import moment from "moment";
import { StyledFooterButton } from "./ExtensionNewTask";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import Switch from "app/popleads/components/ui-kit/Switch";
import useCompletedTasks from "app/popleads/hooks/useGetCompletedTasks";
import useTaskDelete from "app/popleads/hooks/useTaskDelete";
import useTaskComplete from "app/popleads/hooks/useTaskComplete";
import useUpdateTasksTime from "app/popleads/hooks/useUpdateTasksTime";
import { useExtensionContext } from "../ExtensionContext";
import { CardHeader } from "app/popleads/components/ui-kit/Card";
import { POPUP_CONTENTS, tagOptions } from "../utils/constants";
import { toast } from "react-toastify";
import CustomDatePicker from "app/popleads/components/ui-kit/DateInput";
import Modal from "app/popleads/components/ui-kit/Modal";
import { post } from "app/popleads/networking/RequestService";
import ExtensionInputGroup from "./ExtensionInputGroup";
import { apiErrorText } from "app/popleads/networking/apiErrorText";

const TaskListContainer = styled.div`
    height: ${(props) => (props.hasBulkActions ? "calc(100% - 107px)" : "calc(100% - 58px)")};
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
`;

const DateHeaderContainer = styled.div`
    position: sticky;
    top: 0;
    background-color: ${colors.neutral[50]};
    z-index: 10;
    padding: 12px 0;
    padding: 0 12px;
    overflow: visible;
`;

const SelectAllContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0px 12px 5px 12px;
    background-color: ${colors.neutral[50]};
    border-bottom: 1px solid ${colors.neutral[200]};
`;

const SelectAllCheckbox = styled.div`
    width: 15px;
    height: 15px;
    border: 2px solid ${(props) => (props.checked ? colors.l3rnTech[900] : colors.neutral[500])};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: ${(props) => (props.checked ? colors.l3rnTech[900] : "transparent")};
    transition: all 0.2s ease;
    margin-left: 4px;

    &:after {
        content: ${(props) => (props.checked ? "'✓'" : "''")};
        color: ${colors.white};
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
    }
`;

const SelectAllLabel = styled.label`
    color: ${colors.neutral[900]};
    font-size: 12px;
    font-weight: 400;
    cursor: pointer;
    user-select: none;
    margin: 0;
    line-height: 16px;
`;

const BulkActionsContainer = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TasksContainer = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto !important;
    overflow-x: visible !important;
    padding: 0 12px;
    padding-top: 15px;

    &::after {
        content: "";
        display: block;
        height: 60px; /* Spacer to prevent bottom cutoff when expanded */
        width: 100%;
        flex-shrink: 0;
    }
`;

const StyledButtonContainer = styled(StyledFooterButton)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    width: 100%;
`;

const BulkActionsFooter = styled.div`
    position: absolute;
    bottom: 58px;
    left: 0;
    right: 0;
    background-color: ${colors.white};
    border-top: 1px solid ${colors.neutral[200]};
    border-top: 1px solid ${colors.neutral[200]};
    width: 100%;
    box-sizing: border-box;
`;

const FilterBar = styled.div`
    background-color: ${colors.white};
    border-bottom: 1px solid ${colors.neutral[200]};
    padding: 5px;

    .filter-dropdown-container {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    .filter-label {
        color: ${colors.neutral[900]};
        font-size: 14px;
        font-weight: 500;
        min-width: fit-content;
    }
`;

const DashedDivider = styled.hr`
    border: none;
    border-top: 1px dashed ${colors.neutral[200]};
    margin: 0px 0 8px 0;
`;

const TimeDisplay = styled.div`
    color: ${colors.neutral[700]};
    font-size: 11px;
    font-weight: 500;
    width: 30px;
    min-width: 30px;
    text-align: left;
    flex-shrink: 0;
    margin-top: 6px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px;
`;

const NoTasksMessage = styled.div`
    text-align: center;
    padding: 20px;
    color: ${colors.neutral[500]};
`;

const StyledText = styled.div`
    color: ${colors.neutral[900]};
    font-size: 12px;
`;
const StyledFlex = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;
const StyledTaskContainer = styled.div`
    margin-bottom: 0;
    overflow: visible;
    flex: 1;
`;

const RowSelectionCheckbox = styled.div`
    width: 13px;
    height: 13px;
    min-width: 13px;
    min-height: 13px;
    border: 2px solid ${(props) => (props.selected ? "#00856C" : colors.neutral[500])};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: ${(props) => (props.selected ? "#00856C" : "transparent")};
    transition: all 0.2s ease;
    box-sizing: border-box;
    flex-shrink: 0;
    margin-top: 6px;
    position: relative;
    opacity: ${(props) => (props.selected || props.isExpanded ? 1 : 0)};
    visibility: ${(props) => (props.selected || props.isExpanded ? "visible" : "hidden")};

    &:after {
        content: ${(props) => (props.selected ? "'✓'" : "''")};
        color: ${colors.white};
        font-size: 10px;
        font-weight: bold;
        line-height: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const TaskRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 4px;
    position: relative;
    //z-index: ${(props) => (props.isDatePickerOpen || props.isExpanded ? 100 : 1)};

    &:hover ${RowSelectionCheckbox} {
        opacity: 1;
        visibility: visible;
    }
`;

const TimeGroup = styled.div``;

const HeaderTitle = styled.div`
    color: ${colors.neutral[900]};
    font-size: 18px;
    font-weight: 500;
`;

const MoveButtonWrapper = styled.div`
    position: relative;
    padding: 4px 0;
    overflow: visible;
    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: -20px;
        right: -20px;
        height: 100%;
        pointer-events: none;
    }

    &:hover .move-dropdown {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    .move-dropdown:hover {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    .move-dropdown {
        display: ${(props) => (props.showDatePicker ? "none !important" : "none")};
    }
`;

const MoveDropdown = styled.div`
    position: fixed;
    background: ${colors.white};
    border: 1px solid ${colors.neutral[100]};
    border-radius: 6px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    padding: 8px 0;
    display: none;
    max-height: 160px;
    transform: translate(-18px, -194px);

    &:after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid ${colors.white};
    }

    &:before {
        content: "";
        position: absolute;
        bottom: -9px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-top: 9px solid ${colors.neutral[100]};
    }

    &.dropdown-above {
        &:after {
            content: "";
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-bottom: 8px solid ${colors.white};
        }

        &:before {
            content: "";
            position: absolute;
            bottom: -9px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 9px solid transparent;
            border-right: 9px solid transparent;
            border-bottom: 9px solid ${colors.neutral[100]};
        }
    }

    &.dropdown-below {
        &:after {
            content: "";
            position: absolute;
            top: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 8px solid ${colors.white};
        }

        &:before {
            content: "";
            position: absolute;
            top: -9px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 9px solid transparent;
            border-right: 9px solid transparent;
            border-top: 9px solid ${colors.neutral[100]};
        }
    }
`;

const MoveOption = styled.div`
    padding: 8px 16px;
    color: ${colors.neutral[800]};
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral[50]};
    }
`;

const DatePickerWrapper = styled.div`
    position: fixed;
    transform: translateX(-52%);
    //background: ${colors.white};
    border-radius: 8px;
    //box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
    //border: 1px solid ${colors.neutral[200]};
    max-height: 300px;
    max-width: 280px;
    overflow: hidden;
`;

const TaskTypeFilterWrapper = styled.div`
    width: 100%;

    .extension-input-group {
        margin-bottom: 0;

        .select__control {
            min-height: 32px;
            border: 1px solid ${colors.neutral[300]};
            border-radius: 6px;
            font-size: 12px;

            &:hover {
                border-color: ${colors.neutral[400]};
            }

            &--is-focused {
                border-color: ${colors.primary[900]};
                box-shadow: 0 0 0 1px ${colors.primary[900]};
            }
        }

        .select__value-container {
            padding: 2px 8px;
        }

        .select__placeholder {
            color: ${colors.neutral[500]};
            font-size: 12px;
        }

        .select__multi-value {
            font-size: 11px;

            &__label {
                padding: 2px 4px;
            }

            &__remove {
                padding: 0 4px;
            }
        }

        .select__menu {
            font-size: 12px;
        }
    }
`;

const ShowEarlierLink = styled.a`
    color: ${colors.l3rnTech[900]} !important;
    cursor: pointer;
    font-weight: 500;
    font-size: 12px;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        color: ${colors.l3rnTech[900]} !important;
    }
`;

const sortTasksByTime = (tasksToSort, fullDateCompare = false) => {
    return [...tasksToSort].sort((a, b) => {
        const getTime = (dateInput) => {
            if (!dateInput) return 0;
            // Check if it's a number or can be converted to a valid number
            const num = Number(dateInput);
            if (!isNaN(num) && num > 0) {
                return num;
            }
            // Fallback to moment parsing for strings
            const m = moment(dateInput);
            return m.isValid() ? m.valueOf() : 0;
        };

        const valA = getTime(a?.zaman);
        const valB = getTime(b?.zaman);

        if (fullDateCompare) {
            if (valA !== valB) {
                return valA - valB;
            }
            // Fallback to createdTime if zaman matches or is missing
            const createdA = getTime(a?.createdTime);
            const createdB = getTime(b?.createdTime);
            if (createdA !== createdB) {
                return createdA - createdB;
            }
            // Final fallback to ID for deterministic sorting
            return (a.id || "").toString().localeCompare((b.id || "").toString());
        } else {
            // For time-only comparison (HH:mm)
            const timeAFormat = valA ? moment(valA).format("HH:mm") : "00:00";
            const timeBFormat = valB ? moment(valB).format("HH:mm") : "00:00";

            const [hoursA, minutesA] = timeAFormat.split(":").map(Number);
            const [hoursB, minutesB] = timeBFormat.split(":").map(Number);

            const timeAMinutes = hoursA * 60 + minutesA;
            const timeBMinutes = hoursB * 60 + minutesB;

            return timeAMinutes - timeBMinutes;
        }
    });
};

const ExtensionTaskList = ({ taskList, taskListLoading, setTaskToDelete }) => {
    const { getCompletedTasks, loading: completedTasksLoading } = useCompletedTasks();
    const { bulkDeleteTasks, loading: deleteLoading } = useTaskDelete();
    const { bulkCompleteTasks, loading: completeLoading } = useTaskComplete();
    const { updateTasksTime, loading: moveLoading } = useUpdateTasksTime();
    const {
        selectedDate,
        setState,
        liveTasks,
        setLiveTasks,
        selectedTasksId,
        staticTaskList,
        fetchStaticTaskList,
        //staticTaskListLoading,
        modals,
        handleBulkDelete: contextBulkDelete,
        selectedModal,
        modalMessage,
        showOverdueTasks,
        onlyCompletedTasks,
        expandedTaskIds,
    } = useExtensionContext();
    const [tasks, setTasks] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
    const [showFilter, setShowFilter] = useState(false);
    const taskRefs = useRef({});
    const tasksContainerRef = useRef(null);
    const moveButtonRef = useRef(null);
    const moveDropdownRef = useRef(null);
    const datePickerRef = useRef(null);
    //const [expandedTaskIds, setExpandedTaskIds] = useState([]);
    const [datePickerTaskId, setDatePickerTaskId] = useState(null);

    const [overdueTasks, setOverdueTasks] = useState([]);
    const [isOverdueTasksLoading, setIsOverdueTasksLoading] = useState(false);
    const [page, setPage] = useState(0);

    /**
     * Both the overdue header's add button and DateHeader's open the same popup, so they
     * share one handler rather than repeating the payload.
     *
     * The updater form matters here: `setState` is a plain `useState` setter over one
     * state object, so passing an object literal replaces the whole of it and drops every
     * other key — profile, selectedDeal, the open modal and so on. The overdue branch used
     * to do exactly that.
     */
    const openNewTaskPopup = () => {
        setState((prev) => ({
            ...prev,
            popupContent: POPUP_CONTENTS.NEW_TASK,
            popupIsOpen: true,
            taskPage: true,
            editMode: false,
        }));
    };

    const handleSelectedDateChange = (date) => {
        setState((prev) => ({ ...prev, selectedDate: date }));
    };
    const [hasMoreOld, setHasMoreOld] = useState(true);
    const [isOverdueToggleOn, setIsOverdueToggleOn] = useState(() => {
        const saved = localStorage.getItem("isOverdueToggleOn");
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [todayOldTasks, setTodayOldTasks] = useState([]);
    const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);
    const [todayMoreTasks, setTodayMoreTasks] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loaderRef = useRef(null);
    const isActive = onlyCompletedTasks;

    const isSelectedDateToday = useMemo(() => moment(selectedDate).isSame(moment(), "day"), [selectedDate]);

    const overdueTasksForTodayList = useMemo(() => {
        if (!isSelectedDateToday || showOverdueTasks || !taskList?.overdueTasks) {
            return [];
        }
        const markedTasks = taskList.overdueTasks.map((t) => ({
            ...t,
            isFromOverdueApi: true,
        }));
        return sortTasksByTime(markedTasks, true);
    }, [isSelectedDateToday, showOverdueTasks, taskList?.overdueTasks]);

    const selectedDayStart = moment(selectedDate)
        .startOf("day")
        .valueOf();
    const selectedDayEnd = moment(selectedDate)
        .endOf("day")
        .valueOf();

    useEffect(() => {
        fetchStaticTaskList();
    }, [fetchStaticTaskList]);

    const isMountedRef = useRef(false);

    // Tarih değiştiğinde tüm expand edilmiş taskları sıfırla
    // Reset todayOldTasks and todayMoreTasks when date changes or view changes
    useEffect(() => {
        setTodayOldTasks([]);
        setTodayMoreTasks([]);
    }, [selectedDate, isOverdueToggleOn]);

    // Tarih değiştiğinde tüm expand edilmiş taskları sıfırla
    useEffect(() => {
        if (isMountedRef.current) {
            setState((prev) => ({ ...prev, expandedTaskIds: [] }));
        } else {
            isMountedRef.current = true;
        }
    }, [selectedDate]);

    useEffect(() => {
        setLiveTasks([]);
    }, [selectedDate, setLiveTasks]);

    useEffect(() => {
        setSelectedTaskTypes([]);
        setShowFilter(false);
    }, [selectedDate, showOverdueTasks, isActive]);

    useEffect(() => {
        if (selectedTasksId) {
            setState((prev) => {
                const currentExpanded = prev.expandedTaskIds || [];
                if (currentExpanded.includes(selectedTasksId)) return prev;
                return { ...prev, expandedTaskIds: [...currentExpanded, selectedTasksId] };
            });
        }
    }, [selectedTasksId]);

    useEffect(() => {
        if (selectedTasksId && taskRefs.current[selectedTasksId] && tasksContainerRef.current) {
            setTimeout(() => {
                const taskElement = taskRefs.current[selectedTasksId];
                const container = tasksContainerRef.current;

                if (!taskElement || !container) return;

                try {
                    const containerRect = container.getBoundingClientRect();
                    const taskRect = taskElement.getBoundingClientRect();

                    if (containerRect.height === 0 || taskRect.height === 0) return;

                    const scrollTop =
                        container.scrollTop +
                        taskRect.top -
                        containerRect.top -
                        containerRect.height / 2 +
                        taskRect.height / 2;

                    container.scrollTo({
                        top: Math.max(0, scrollTop),
                        behavior: "smooth",
                    });
                } catch (error) {
                    toast.error("Scroll to task failed");
                }
            }, 100);
        }
    }, [selectedTasksId, tasks]);

    const hasScrolledRef = useRef(false);

    useEffect(() => {
        hasScrolledRef.current = false;
    }, [selectedDate]);

    useEffect(() => {
        if (!hasScrolledRef.current && tasks.length > 0 && expandedTaskIds.length > 0 && tasksContainerRef.current) {
            const firstExpandedTask = tasks.find((t) => expandedTaskIds.includes(t.id));

            if (firstExpandedTask && taskRefs.current[firstExpandedTask.id]) {
                hasScrolledRef.current = true;
                setTimeout(() => {
                    const taskElement = taskRefs.current[firstExpandedTask.id];
                    const container = tasksContainerRef.current;

                    if (!taskElement || !container) return;

                    try {
                        const containerRect = container.getBoundingClientRect();
                        const taskRect = taskElement.getBoundingClientRect();

                        const scrollTop =
                            container.scrollTop +
                            taskRect.top -
                            containerRect.top -
                            containerRect.height / 2 +
                            taskRect.height / 2;

                        container.scrollTo({
                            top: Math.max(0, scrollTop),
                            behavior: "smooth",
                        });
                    } catch (error) {
                        // ignore
                    }
                }, 100);
            }
        }
    }, [tasks, expandedTaskIds]);

    useEffect(() => {
        const handleTasks = async () => {
            if (isActive) {
                const completedTasksData = await getCompletedTasks({ selectedDate });
                if (completedTasksData?.completedTasks?.length > 0) {
                    const sortedTasks = sortTasksByTime(completedTasksData.completedTasks);
                    setTasks(sortedTasks);
                    setSelectedTasks([]);
                } else {
                    setTasks([]);
                    setSelectedTasks([]);
                }
            } else if (showOverdueTasks && overdueTasks?.length > 0) {
                setTasks(overdueTasks);
                setSelectedTasks([]);
            } else if (isSelectedDateToday && !showOverdueTasks) {
                const todayGorev = taskList?.gorev?.length > 0 ? sortTasksByTime(taskList.gorev) : [];
                let merged = [...todayGorev];

                if (todayMoreTasks.length > 0) {
                    const sortedMore = sortTasksByTime(todayMoreTasks);
                    merged = [...merged, ...sortedMore];
                }

                if (isOverdueToggleOn) {
                    let allOverdues = [];
                    if (overdueTasksForTodayList.length > 0) {
                        allOverdues = [...overdueTasksForTodayList];
                    }
                    if (todayOldTasks.length > 0) {
                        allOverdues = [...allOverdues, ...todayOldTasks];
                    }

                    if (allOverdues.length > 0) {
                        const taskIds = new Set(todayGorev.map((t) => t.id));
                        const uniqueOverdues = allOverdues.filter((t) => !taskIds.has(t.id));

                        // Re-sort overdues to ensure correct order
                        const sortedOverdues = sortTasksByTime(uniqueOverdues, true);

                        merged = [...sortedOverdues, ...merged];
                    }
                }
                setTasks(merged);
                setSelectedTasks([]);
            } else if (taskList?.gorev?.length > 0 && !showOverdueTasks) {
                const sortedTasks = sortTasksByTime(taskList.gorev);
                let merged = [...sortedTasks];
                if (todayMoreTasks.length > 0) {
                    const sortedMore = sortTasksByTime(todayMoreTasks);
                    merged = [...merged, ...sortedMore];
                }
                setTasks(merged);
                setSelectedTasks([]);
            } else {
                setTasks([]);
                setSelectedTasks([]);
            }
        };

        handleTasks();
    }, [
        isActive,
        selectedDate,
        taskList?.gorev,
        overdueTasks,
        showOverdueTasks,
        taskListLoading,
        isSelectedDateToday,
        overdueTasksForTodayList,
        isOverdueToggleOn,
        todayOldTasks,
        todayMoreTasks,
    ]);

    useEffect(() => {
        if (liveTasks.length > 0) {
            const filteredLiveTasks = liveTasks.filter(
                (task) => task.zaman >= selectedDayStart && task.zaman <= selectedDayEnd
            );
            if (filteredLiveTasks.length > 0) {
                const findTask = taskList?.gorev?.some((task) => task.id === filteredLiveTasks[0].id);
                if (!findTask) {
                    const newTasks = [...tasks, ...filteredLiveTasks];
                    const sortedTasks = sortTasksByTime(newTasks);
                    setTasks(sortedTasks);
                }
            }
            setLiveTasks([]);
        }
    }, [tasks, liveTasks]);

    const handleTaskCompletion = (taskId) => {
        if (taskId === null) {
            setSelectedTasks((prev) => prev.filter((id) => id !== taskId));
            return;
        }

        setSelectedTasks((prev) => {
            if (prev.includes(taskId)) {
                return prev.filter((id) => id !== taskId);
            } else {
                return [...prev, taskId];
            }
        });
    };

    const groupTasksByTime = (tasks) => {
        if (showOverdueTasks) {
            return { "": tasks };
        }

        if (isSelectedDateToday && tasks.some((t) => moment(t.zaman).valueOf() < selectedDayStart)) {
            const overdues = tasks.filter((t) => moment(t.zaman).valueOf() < selectedDayStart);
            const todayTasks = tasks.filter((t) => moment(t.zaman).valueOf() >= selectedDayStart);
            const groups = {};
            if (overdues.length > 0) {
                groups[""] = overdues;
            }
            todayTasks.forEach((task) => {
                const timeKey = moment(task.zaman).format("hh:mm A");
                if (!groups[timeKey]) {
                    groups[timeKey] = [];
                }
                groups[timeKey].push(task);
            });
            return groups;
        }

        const groups = {};
        tasks.forEach((task) => {
            const timeKey = moment(task.zaman).format("hh:mm A");

            if (!groups[timeKey]) {
                groups[timeKey] = [];
            }
            groups[timeKey].push(task);
        });
        return groups;
    };

    /*const groupTasksByDate = (tasks) => {
        const groups = {};
        tasks.forEach((task) => {
            const dateKey = moment(task.zaman).format("MMM DD, YYYY");
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(task);
        });
        return groups;
    };*/

    const fetchOverdueTasks = async (page) => {
        if (isOverdueTasksLoading) return;
        setIsOverdueTasksLoading(true);
        try {
            const res = await post({
                fn: "gorevListe",
                tarih: moment().valueOf(),
                old: 1,
                start: page === 0 ? 0 : -1,
                startOld: page * 50,
            });
            if (res?.data?.overdueTasks?.length > 0) {
                const markedTasks = res.data.overdueTasks.map((t) => ({ ...t, isFromOverdueApi: true }));
                const sortedTasks = sortTasksByTime(markedTasks, true);
                setOverdueTasks((prev) => (page === 0 ? sortedTasks : [...prev, ...sortedTasks]));
                setHasMoreOld(res?.data?.hasMoreOld > 0);
                setPage(page + 1);
            }
        } catch (error) {
            toast.error(apiErrorText(error));
        } finally {
            setIsOverdueTasksLoading(false);
        }
    };

    const handleShowEarlier = async () => {
        if (isLoadingEarlier) return;
        setIsLoadingEarlier(true);
        try {
            // Calculate startOld based on currently loaded overdue tasks (initial + manually fetched)
            // Initial batch is usually 50 (from taskList.overdueTasks)
            const currentCount = (taskList?.overdueTasks?.length || 0) + todayOldTasks.length;

            const res = await post({
                fn: "gorevListe",
                tarih: moment(selectedDate).valueOf(),
                old: 1,
                start: -1,
                startOld: currentCount,
            });

            if (res?.data?.overdueTasks?.length > 0) {
                const markedTasks = res.data.overdueTasks.map((t) => ({ ...t, isFromOverdueApi: true }));
                setTodayOldTasks((prev) => [...prev, ...markedTasks]);
            }
        } catch (error) {
            //console.error(error);
            toast.error(apiErrorText(error));
        } finally {
            setIsLoadingEarlier(false);
        }
    };

    const handleShowMore = async () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const currentCount = (taskList?.gorev?.length || 0) + todayMoreTasks.length;

            const res = await post({
                fn: "gorevListe",
                tarih: moment(selectedDate).valueOf(),
                old: 1,
                start: currentCount,
                startOld: -1,
            });

            if (res?.data?.gorev?.length > 0) {
                const markedTasks = res.data.gorev.map((t) => ({ ...t, isFromMoreApi: true }));
                setTodayMoreTasks((prev) => [...prev, ...markedTasks]);
            }
        } catch (error) {
            toast.error(apiErrorText(error));
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        if (showOverdueTasks && overdueTasks.length === 0) {
            fetchOverdueTasks(0);
        }
    }, [showOverdueTasks, overdueTasks.length]);

    useEffect(() => {
        if (!showOverdueTasks || !hasMoreOld || isOverdueTasksLoading || page === 0) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                fetchOverdueTasks(page);
            }
        });

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) observer.unobserve(loaderRef.current);
        };
    }, [hasMoreOld, isOverdueTasksLoading, page, taskListLoading]);

    // Handle dropdown positioning
    useEffect(() => {
        const handleDropdownPosition = () => {
            if (moveButtonRef.current && moveDropdownRef.current) {
                const buttonRect = moveButtonRef.current.getBoundingClientRect();
                const dropdown = moveDropdownRef.current;

                // Calculate dropdown height (approximate)
                const dropdownHeight = 120; // max-height from CSS
                const spaceAbove = buttonRect.top;

                // Position dropdown above the button
                dropdown.style.left = `${buttonRect.left + buttonRect.width / 2}px`;

                // Remove existing classes
                dropdown.classList.remove("dropdown-above", "dropdown-below");

                if (spaceAbove >= dropdownHeight) {
                    // Enough space above, position above button
                    dropdown.style.top = `${buttonRect.top - dropdownHeight - 8}px`;
                    dropdown.style.bottom = "auto";
                    dropdown.classList.add("dropdown-above");
                } else {
                    // Not enough space above, position below button
                    dropdown.style.top = `${buttonRect.bottom + 8}px`;
                    dropdown.style.bottom = "auto";
                    dropdown.classList.add("dropdown-below");
                }

                dropdown.style.transform = "translateX(-50%)";
            }
        };

        // Add event listeners for hover
        const moveButton = moveButtonRef.current;
        if (moveButton) {
            moveButton.addEventListener("mouseenter", handleDropdownPosition);
            return () => {
                moveButton.removeEventListener("mouseenter", handleDropdownPosition);
            };
        }
    }, []);

    // Handle window resize to reposition dropdown
    useEffect(() => {
        const handleResize = () => {
            if (moveDropdownRef.current && moveDropdownRef.current.style.display === "block") {
                const moveButton = moveButtonRef.current;
                if (moveButton) {
                    const buttonRect = moveButton.getBoundingClientRect();
                    const dropdown = moveDropdownRef.current;

                    // Calculate dropdown height (approximate)
                    const dropdownHeight = 120; // max-height from CSS
                    const spaceAbove = buttonRect.top;

                    dropdown.style.left = `${buttonRect.left + buttonRect.width / 2}px`;

                    // Remove existing classes
                    dropdown.classList.remove("dropdown-above", "dropdown-below");

                    if (spaceAbove >= dropdownHeight) {
                        // Enough space above, position above button
                        dropdown.style.top = `${buttonRect.top - dropdownHeight - 8}px`;
                        dropdown.style.bottom = "auto";
                        dropdown.classList.add("dropdown-above");
                    } else {
                        // Not enough space above, position below button
                        dropdown.style.top = `${buttonRect.bottom + 8}px`;
                        dropdown.style.bottom = "auto";
                        dropdown.classList.add("dropdown-below");
                    }
                }
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Handle date picker positioning
    useEffect(() => {
        if (showDatePicker && moveButtonRef.current && datePickerRef.current) {
            const buttonRect = moveButtonRef.current.getBoundingClientRect();
            const datePicker = datePickerRef.current;

            // Position date picker above the button
            let leftPos = buttonRect.left + buttonRect.width / 2;
            const datePickerWidth = 280; // max-width from CSS

            // Boundary check
            if (leftPos + datePickerWidth / 2 > window.innerWidth - 10) {
                leftPos = window.innerWidth - datePickerWidth / 2 - 10;
            }
            if (leftPos - datePickerWidth / 2 < 10) {
                leftPos = datePickerWidth / 2 + 10;
            }

            datePicker.style.left = `${leftPos}px`;
            datePicker.style.top = `${buttonRect.top - 5}px`;
            datePicker.style.transform = "translateX(-52%) translateY(-100%)";
        }
    }, [showDatePicker]);

    /*const handleShowOverdueTasks = () => {
        setState((prevState) => ({
            ...prevState,
            showOverdueTasks: true,
        }));
        setIsActive(false);
        fetchOverdueTasks(0);
    };*/

    const handleShowTodayTasks = () => {
        setState((prevState) => ({
            ...prevState,
            showOverdueTasks: false,
            selectedDate: new Date(),
            onlyCompletedTasks: false,
        }));
    };

    const highlightedDates = useMemo(() => {
        if (!staticTaskList?.gorev && !staticTaskList?.overdueTasks) return [];

        const dates = [];

        if (staticTaskList?.gorev) {
            staticTaskList.gorev.forEach((task) => {
                const date = moment(task?.zaman).format("YYYY-MM-DD");
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });
        }

        if (staticTaskList?.overdueTasks) {
            staticTaskList.overdueTasks.forEach((task) => {
                const date = moment(task?.zaman).format("YYYY-MM-DD");
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });
        }

        return dates;
    }, [staticTaskList?.gorev, staticTaskList?.overdueTasks]);

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        if (newSelectAll) {
            // Select all visible/filtered tasks
            const allVisibleTaskIds = filteredTasks.map((task) => task.id);
            setSelectedTasks(allVisibleTaskIds);
        } else {
            setSelectedTasks([]);
        }
    };

    useEffect(() => {
        const visibleTasks = filteredTasks.length > 0 ? filteredTasks : tasks;

        if (visibleTasks.length === 0) {
            setSelectAll(false);
        } else if (
            selectedTasks.length === visibleTasks.length &&
            selectedTasks.every((id) => visibleTasks.some((task) => task.id === id))
        ) {
            setSelectAll(true);
        } else {
            setSelectAll(false);
        }
    }, [selectedTasks, tasks, filteredTasks]);

    const handleDelete = () => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to delete");
            return;
        }

        setState({
            selectedModal: modals.DELETE_BULK_TASKS,
            modalMessage: "Are you sure want to delete all selected task?",
        });
    };

    const handleBulkDelete = async () => {
        const onSuccess = () => {
            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);
        };

        await contextBulkDelete(selectedTasks, bulkDeleteTasks, onSuccess);
    };

    const handleComplete = async () => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to complete");
            return;
        }

        try {
            await bulkCompleteTasks(selectedTasks);

            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);

            toast.success(`${selectedTasks.length} task(s) completed successfully`);

            fetchStaticTaskList();
        } catch (error) {
            toast.error(apiErrorText(error));
        }
    };

    const handleMove = async (days) => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to move");
            return;
        }

        try {
            if (days === "other") {
                setShowDatePicker(true);
                return;
            }

            // For overdue tasks, calculate new date based on today + period
            // For regular tasks, use period-based movement

            // Check if we're dealing with overdue tasks
            const firstTask = tasks.find((task) => selectedTasks.includes(task.id));
            const isOverdue = firstTask && moment(firstTask.zaman).isBefore(moment(), "day");

            if (showOverdueTasks || isOverdue) {
                // For overdue tasks: today + days, preserving original time
                if (firstTask) {
                    const originalTime = moment(firstTask.zaman);
                    const today = moment().startOf("day");
                    const newDate = today
                        .add(days, "days")
                        .hour(originalTime.hour())
                        .minute(originalTime.minute())
                        .second(originalTime.second())
                        .valueOf();
                    await updateTasksTime(selectedTasks, { zaman: newDate });
                }
            } else {
                // For future tasks: use period-based movement
                await updateTasksTime(selectedTasks, { period: days });
            }

            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);

            toast.success(`${selectedTasks.length} task(s) moved by ${days} day${days > 1 ? "s" : ""} successfully`);

            fetchStaticTaskList();
        } catch (error) {
            toast.error(apiErrorText(error));
        }
    };

    const handleDateChange = async (date) => {
        if (date && selectedTasks.length > 0) {
            try {
                const selectedDateMs = new Date(date).getTime();

                await updateTasksTime(selectedTasks, { zaman: selectedDateMs });

                const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
                setTasks(remainingTasks);
                setSelectedTasks([]);
                setSelectAll(false);
                setShowDatePicker(false);

                toast.success(
                    `${selectedTasks.length} task(s) moved to ${moment(date).format("MMM DD, YYYY")} successfully`
                );

                fetchStaticTaskList();
            } catch (error) {
                toast.error(apiErrorText(error));
            }
        }
    };

    const handleFilterToggle = () => {
        setShowFilter(!showFilter);
    };

    const filteredTasks = useMemo(() => {
        if (selectedTaskTypes.length === 0) {
            return tasks;
        }

        const selectedTypes = selectedTaskTypes.map((option) => option.value);

        const filtered = tasks.filter((task) => {
            return task.type && selectedTypes.includes(task.type);
        });

        return filtered;
    }, [tasks, selectedTaskTypes]);

    return (
        <>
            <TaskListContainer hasBulkActions={selectedTasks.length > 0 || selectAll} ref={tasksContainerRef}>
                <DateHeaderContainer>
                    {showOverdueTasks ? (
                        <CardHeader
                            cardTitle={<HeaderTitle>All Overdues</HeaderTitle>}
                            button={
                                <ButtonWithText
                                    label="Add task"
                                    onClick={openNewTaskPopup}
                                    buttonStyle="default"
                                    icon="add"
                                    iconType="material"
                                    variant="l3rnTech"
                                    type="default"
                                    size="circle"
                                />
                            }
                        />
                    ) : (
                        <DateHeader
                            value={selectedDate}
                            onChange={handleSelectedDateChange}
                            onAddClick={openNewTaskPopup}
                            highlightedDates={!taskListLoading ? highlightedDates : undefined}
                        />
                    )}
                </DateHeaderContainer>
                {!taskListLoading && tasks.length > 0 && (
                    <>
                        <SelectAllContainer>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <SelectAllCheckbox
                                    checked={selectAll}
                                    onClick={handleSelectAll}
                                    role="checkbox"
                                    aria-checked={selectAll}
                                />
                                <SelectAllLabel onClick={handleSelectAll}>Select All Tasks</SelectAllLabel>
                            </div>
                            <ButtonWithText
                                label="Toggle filters"
                                buttonStyle={"inverted"}
                                variant="l3rnTech"
                                size="circle"
                                type="default"
                                icon="Filters2"
                                iconType="custom"
                                onClick={handleFilterToggle}
                            ></ButtonWithText>
                        </SelectAllContainer>
                    </>
                )}
                {showFilter && (
                    <FilterBar>
                        <TaskTypeFilterWrapper>
                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label={"Task Type"}
                                type="select"
                                placeholder="Please Select Task Type"
                                options={tagOptions}
                                isMulti={true}
                                onChange={(selected) => {
                                    setSelectedTaskTypes(selected || []);
                                }}
                                value={selectedTaskTypes}
                                hideLabel={true}
                            />
                        </TaskTypeFilterWrapper>
                    </FilterBar>
                )}
                <TasksContainer>
                    {taskListLoading ||
                    completedTasksLoading ||
                    (showOverdueTasks && isOverdueTasksLoading && tasks.length === 0) ||
                    (isSelectedDateToday && !showOverdueTasks && taskListLoading && tasks.length === 0) ? (
                        <LoadingContainer>
                            <Spinner size={32} thickness={4} variant="l3rnTech" />
                        </LoadingContainer>
                    ) : (
                        <div>
                            {isSelectedDateToday && !showOverdueTasks && isOverdueToggleOn && !showFilter && (
                                <>
                                    {(() => {
                                        const totalOld = taskList?.oldTasksInTotal || 0;
                                        const currentLoaded =
                                            (taskList?.overdueTasks?.length || 0) + todayOldTasks.length;
                                        const remaining = Math.max(0, totalOld - currentLoaded);

                                        if (remaining > 0) {
                                            return (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        paddingBottom: "8px",
                                                    }}
                                                >
                                                    <ShowEarlierLink onClick={handleShowEarlier}>
                                                        {isLoadingEarlier ? (
                                                            <Spinner size={16} thickness={3} variant="l3rnTech" />
                                                        ) : (
                                                            `Show Earlier (${remaining})`
                                                        )}
                                                    </ShowEarlierLink>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </>
                            )}
                            {!tasks.length ? (
                                <NoTasksMessage>No tasks found</NoTasksMessage>
                            ) : selectedTaskTypes.length > 0 && !filteredTasks.length ? (
                                <NoTasksMessage>No tasks match the selected task types</NoTasksMessage>
                            ) : (
                                <div>
                                    {(() => {
                                        let globalIndex = 0;
                                        return Object.entries(groupTasksByTime(filteredTasks)).map(
                                            ([time, groupTasks]) => (
                                                <TimeGroup key={time || "overdue"}>
                                                    {time !== "" && <DashedDivider />}
                                                    {groupTasks.map((task, idx) => {
                                                        const currentGlobalIndex = globalIndex;
                                                        globalIndex++;
                                                        const taskTime =
                                                            task?.zaman && !task.isFromOverdueApi
                                                                ? moment(task.zaman).format("HH:mm")
                                                                : "";
                                                        const isSelected = selectedTasks.includes(task.id);
                                                        return (
                                                            <TaskRow
                                                                key={task.id}
                                                                isExpanded={expandedTaskIds.includes(task.id)}
                                                                isDatePickerOpen={datePickerTaskId === task.id}
                                                                index={currentGlobalIndex}
                                                            >
                                                                <TimeDisplay>{taskTime}</TimeDisplay>
                                                                <RowSelectionCheckbox
                                                                    className="selection-circle"
                                                                    selected={isSelected}
                                                                    isExpanded={expandedTaskIds.includes(task.id)}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleTaskCompletion(task.id);
                                                                    }}
                                                                />
                                                                <StyledTaskContainer
                                                                    ref={(el) => {
                                                                        if (el) {
                                                                            taskRefs.current[task.id] = el;
                                                                        }
                                                                    }}
                                                                >
                                                                    <SelectableTaskCard
                                                                        task={task}
                                                                        index={currentGlobalIndex}
                                                                        setTaskToDelete={setTaskToDelete}
                                                                        selectedTasks={selectedTasks}
                                                                        onTaskComplete={handleTaskCompletion}
                                                                        overdue={
                                                                            showOverdueTasks ||
                                                                            (!showOverdueTasks &&
                                                                                moment(task.zaman).isBefore(moment()))
                                                                        }
                                                                        greyMode={selectedTasks.length > 0}
                                                                        hideBulkActions={
                                                                            selectedTasks.length > 1 || selectAll
                                                                        }
                                                                        isCompletedTask={isActive}
                                                                        isFirst={true}
                                                                        isInOverduesPage={showOverdueTasks}
                                                                        isExpanded={expandedTaskIds.includes(task.id)}
                                                                        onToggleExpand={() =>
                                                                            setState((prev) => {
                                                                                const currentExpanded =
                                                                                    prev.expandedTaskIds || [];
                                                                                const newExpanded = currentExpanded.includes(
                                                                                    task.id
                                                                                )
                                                                                    ? currentExpanded.filter(
                                                                                          (id) => id !== task.id
                                                                                      )
                                                                                    : [...currentExpanded, task.id];
                                                                                return {
                                                                                    ...prev,
                                                                                    expandedTaskIds: newExpanded,
                                                                                };
                                                                            })
                                                                        }
                                                                        onDatePickerToggle={(isOpen) =>
                                                                            setDatePickerTaskId(isOpen ? task.id : null)
                                                                        }
                                                                    />
                                                                </StyledTaskContainer>
                                                            </TaskRow>
                                                        );
                                                    })}
                                                </TimeGroup>
                                            )
                                        );
                                    })()}
                                    {!showOverdueTasks &&
                                        !showFilter &&
                                        (() => {
                                            const totalTasks =
                                                taskList?.tasksInTotal ||
                                                taskList?.total ||
                                                taskList?.count ||
                                                taskList?.gorevTotal ||
                                                0;
                                            const currentLoaded =
                                                (taskList?.gorev?.length || 0) + todayMoreTasks.length;
                                            const remaining = Math.max(0, totalTasks - currentLoaded);

                                            if (remaining > 0) {
                                                return (
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            paddingTop: "8px",
                                                            paddingBottom: "8px",
                                                        }}
                                                    >
                                                        <ShowEarlierLink onClick={handleShowMore}>
                                                            {isLoadingMore ? (
                                                                <Spinner size={16} thickness={3} variant="l3rnTech" />
                                                            ) : (
                                                                `Show More (${remaining})`
                                                            )}
                                                        </ShowEarlierLink>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    <div ref={loaderRef} style={{ display: "flex", justifyContent: "center" }}>
                                        {showOverdueTasks && hasMoreOld && (
                                            <Spinner size={32} thickness={4} variant="primary" />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </TasksContainer>
            </TaskListContainer>

            {(selectedTasks.length > 0 || selectAll) && (
                <BulkActionsFooter data-tasks-bulk-actions>
                    <BulkActionsContainer>
                        {!isActive && (
                            <ButtonWithText
                                size="small"
                                variant="success"
                                buttonStyle="borderless"
                                onClick={handleComplete}
                                disabled={selectedTasks.length === 0 || completeLoading}
                            >
                                {completeLoading ? "Completing..." : "Complete"}
                            </ButtonWithText>
                        )}
                        <MoveButtonWrapper ref={moveButtonRef} showDatePicker={showDatePicker}>
                            <ButtonWithText
                                size="small"
                                variant="passive"
                                buttonStyle="borderless"
                                disabled={selectedTasks.length === 0 || moveLoading}
                            >
                                {moveLoading ? "Moving..." : "Move"}
                            </ButtonWithText>
                            {!showDatePicker && (
                                <MoveDropdown ref={moveDropdownRef} className="move-dropdown">
                                    <MoveOption onClick={() => handleMove(1)}>+1 day</MoveOption>
                                    <MoveOption onClick={() => handleMove(3)}>+3 days</MoveOption>
                                    <MoveOption onClick={() => handleMove(7)}>+7 days</MoveOption>
                                    <MoveOption onClick={() => handleMove("other")}>Other</MoveOption>
                                </MoveDropdown>
                            )}
                            {showDatePicker && (
                                <DatePickerWrapper ref={datePickerRef}>
                                    <CustomDatePicker
                                        variant="move-dropdown"
                                        componentVariant={"l3rnTech"}
                                        onChange={handleDateChange}
                                        onClickOutside={() => setShowDatePicker(false)}
                                    />
                                </DatePickerWrapper>
                            )}
                        </MoveButtonWrapper>
                        <ButtonWithText
                            size="small"
                            variant="danger"
                            buttonStyle="borderless"
                            onClick={handleDelete}
                            disabled={selectedTasks.length === 0 || deleteLoading}
                        >
                            {deleteLoading ? "Deleting..." : "Delete"}
                        </ButtonWithText>
                    </BulkActionsContainer>
                </BulkActionsFooter>
            )}

            <StyledButtonContainer data-tasks-footer>
                {showOverdueTasks ? (
                    <ButtonWithText
                        size="xsmall"
                        variant="l3rnTech"
                        buttonStyle="borderless"
                        onClick={handleShowTodayTasks}
                    >
                        Today
                    </ButtonWithText>
                ) : (
                    <>
                        <StyledFlex>
                            <StyledText>Overdues</StyledText>
                            <Switch
                                variant="l3rnTech"
                                isActive={isSelectedDateToday ? isOverdueToggleOn : false}
                                loading={taskListLoading}
                                disabled={isActive}
                                onClick={() => {
                                    if (!isSelectedDateToday) {
                                        toast.warning("Overdues toggle is only available for Today");
                                        return;
                                    }
                                    const newValue = !isOverdueToggleOn;
                                    setIsOverdueToggleOn(newValue);
                                    localStorage.setItem("isOverdueToggleOn", JSON.stringify(newValue));
                                }}
                            />
                        </StyledFlex>
                        <StyledFlex>
                            <StyledText>Only Completed</StyledText>
                            <Switch
                                variant="l3rnTech"
                                isActive={isActive}
                                loading={completedTasksLoading}
                                disabled={isSelectedDateToday && isOverdueToggleOn}
                                onClick={() => {
                                    const newValue = !isActive;
                                    setState((prevState) => ({
                                        ...prevState,
                                        onlyCompletedTasks: newValue,
                                        showOverdueTasks: newValue ? false : prevState.showOverdueTasks,
                                    }));
                                }}
                            />
                        </StyledFlex>
                    </>
                )}
            </StyledButtonContainer>
            <Modal
                isOpen={selectedModal === modals.DELETE_BULK_TASKS}
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }))
                }
                title={modalMessage || "Are you sure want to delete all selected task?"}
                variant="danger"
                icon="delete"
                onSuccess={() => {
                    handleBulkDelete();
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }));
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionTaskList;
